import { useState } from 'react';
import { CURRENCIES, CATEGORIES, INCOME_CATEGORIES, IRS_CATEGORIES, IRS_MILEAGE_RATE, BIZ_CLASS, CLASS_SELECT_KEYS, CATEGORY_KEY_BY_LABEL, INCOME_CAT_KEY_BY_LABEL } from '../utils/constants';
import { makeKey } from '../utils/categoryHelpers';
import { HelpTip } from './HelpTip';
import { TIPS } from '../help/content';

const INCOME_CLASS_KEYS = new Set(['business_income', 'personal_income', 'unclassified_credit']);

function ExpenseForm({ initialForm, onSave, onCancel, categories, addCategory }) {
  const allExpenseCategories = categories || CATEGORIES;
  const canAddCategory = !!addCategory;

  const [form, setForm] = useState(initialForm || {
    vendor: "",
    classification: "business_expense",
    category: "food_dining",
    subtotal: "",
    tax: "",
    tip: "",
    total: "",
    currency: "USD",
    date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    notes: "",
    status: "success",
    imagePreview: null,
  });

  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatTax, setNewCatTax] = useState("Uncategorized");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isIncome = INCOME_CLASS_KEYS.has(form.businessClassification);
  const allCategories = isIncome ? INCOME_CATEGORIES : allExpenseCategories;

  const toKey = (label) => CATEGORY_KEY_BY_LABEL[label] ?? INCOME_CAT_KEY_BY_LABEL[label] ?? makeKey(label);

  const handleClassificationChange = (newClass) => {
    const newIsIncome = INCOME_CLASS_KEYS.has(newClass);
    setForm(f => {
      const update = { ...f, businessClassification: newClass || undefined };
      if (newIsIncome !== INCOME_CLASS_KEYS.has(f.businessClassification)) {
        const validCats = newIsIncome ? INCOME_CATEGORIES : allExpenseCategories;
        const validKeys = validCats.map(toKey);
        if (!validKeys.includes(f.category)) update.category = validKeys[0] ?? '';
      }
      return update;
    });
  };

  const handleCategoryChange = (value) => {
    setForm(f => ({
      ...f,
      category: value,
      ...(value === 'mileage' && !f.mileageRate ? { mileageRate: IRS_MILEAGE_RATE } : {}),
    }));
  };

  // Auto-calc total from mileage fields
  const setMileageField = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value };
      const miles = parseFloat(updated.miles) || 0;
      const rate  = parseFloat(updated.mileageRate) || 0;
      if (miles > 0 && rate > 0) updated.total = (miles * rate).toFixed(2);
      return updated;
    });
  };

  // Auto-calc total from components; used by subtotal/tax/tip onChange
  const setComponent = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value };
      const sum =
        (parseFloat(updated.subtotal) || 0) +
        (parseFloat(updated.tax)      || 0) +
        (parseFloat(updated.tip)      || 0);
      if (sum > 0) updated.total = sum.toFixed(2);
      return updated;
    });
  };

  const componentSum =
    (parseFloat(form.subtotal) || 0) +
    (parseFloat(form.tax)      || 0) +
    (parseFloat(form.tip)      || 0);
  const totalVal = parseFloat(form.total) || 0;
  const hasMismatch = componentSum > 0 && totalVal > 0 && Math.abs(totalVal - componentSum) > 0.01;

  const handleSubmit = () => {
    if (!form.vendor || !form.total) {
      alert("Please fill in vendor and total amount");
      return;
    }
    onSave(form);
  };

  const handleAddCategory = () => {
    const trimmed = newCatName.trim();
    if (!trimmed) return;
    if (addCategory) addCategory(trimmed, newCatTax);
    set("category", makeKey(trimmed));
    setNewCatName("");
    setNewCatTax("Uncategorized");
    setShowNewCat(false);
  };

  return (
    <div style={{ padding: "20px" }}>
      <h3 style={{ margin: "0 0 20px", fontSize: 18, fontWeight: 800, color: "#111" }}>
        Add Transaction
      </h3>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <label style={labelStyle}>Vendor</label>
          <input
            style={inputStyle}
            value={form.vendor || ""}
            onChange={(e) => set("vendor", e.target.value)}
            placeholder="e.g. Starbucks"
          />
        </div>

        <div>
          <label style={labelStyle}>Location</label>
          <input
            style={inputStyle}
            value={form.location || ""}
            onChange={(e) => set("location", e.target.value)}
            placeholder="e.g. San Diego, CA"
          />
        </div>


        {/* Business classification */}
        {(() => {
          const cls = form.businessClassification;
          const effectiveVal = cls === 'unclassified' ? 'unclassified_debit' : (cls || form.classification || 'business_expense');
          const c = cls ? (BIZ_CLASS[cls] || BIZ_CLASS.unclassified_debit) : null;
          return (
            <div>
              <label style={labelStyle}>Classification</label>
              <select
                style={{ ...inputStyle, ...(c ? { borderColor: c.color, background: c.bg, color: c.color, fontWeight: 700 } : {}) }}
                value={effectiveVal}
                onChange={e => handleClassificationChange(e.target.value)}
              >
                {CLASS_SELECT_KEYS.map(k => (
                  <option key={k} value={k}>{BIZ_CLASS[k].label}</option>
                ))}
              </select>
            </div>
          );
        })()}

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <label style={{ ...labelStyle, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              Category <HelpTip content={TIPS.customCategories} placement="top" />
            </label>
            {canAddCategory ? (
              <button
                type="button"
                onClick={() => setShowNewCat(v => !v)}
                style={linkBtnStyle}
              >
                {showNewCat ? "Cancel" : "+ New category"}
              </button>
            ) : (
              <span style={{ fontSize: 11, color: "#9ca3af" }}>Sign in to create categories</span>
            )}
          </div>

          {showNewCat ? (
            <div style={newCatBoxStyle}>
              <input
                style={{ ...inputStyle, marginBottom: 8 }}
                placeholder="Category name"
                value={newCatName}
                onChange={e => setNewCatName(e.target.value)}
                autoFocus
              />
              <label style={labelStyle}>IRS Tax Category</label>
              <select
                style={{ ...inputStyle, marginBottom: 8 }}
                value={newCatTax}
                onChange={e => setNewCatTax(e.target.value)}
              >
                {IRS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                type="button"
                onClick={handleAddCategory}
                style={addBtnStyle}
              >
                Add Category
              </button>
            </div>
          ) : (
            <select
              style={inputStyle}
              value={form.category || ""}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {allCategories.map((cat) => {
                const key = toKey(cat);
                return <option key={key} value={key}>{cat}</option>;
              })}
            </select>
          )}
        </div>

        {/* Tax category override — shown for "Other" expenses */}
        {!isIncome && form.category === 'other' && (
          <div>
            <label style={labelStyle}>IRS Tax Category</label>
            <select
              style={{ ...inputStyle, borderColor: "#a78bfa" }}
              value={form.taxCategory || "Uncategorized"}
              onChange={(e) => set("taxCategory", e.target.value)}
            >
              {IRS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
              Override the IRS deduction category for this specific expense.
            </div>
          </div>
        )}

        {/* Mileage fields */}
        {!isIncome && form.category === 'mileage' && (
          <div style={{ background: "#EFF6FF", border: "1.5px solid #93C5FD", borderRadius: 12, padding: "14px 14px 10px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#2563EB", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>
              Mileage Details
            </div>
            <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Miles</label>
                <input
                  style={inputStyle}
                  type="number"
                  step="0.1"
                  value={form.miles || ""}
                  onChange={(e) => setMileageField("miles", e.target.value)}
                  placeholder="0.0"
                />
              </div>
              <div style={{ flex: 2 }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 4 }}>
                  Rate / mi ($) <HelpTip content={TIPS.mileageRate} placement="top" />
                </label>
                <input
                  style={inputStyle}
                  type="number"
                  step="0.001"
                  value={form.mileageRate || ""}
                  onChange={(e) => setMileageField("mileageRate", e.target.value)}
                  placeholder={IRS_MILEAGE_RATE}
                />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>Origin</label>
              <input
                style={{ ...inputStyle, marginBottom: 8 }}
                value={form.mileageOrigin || ""}
                onChange={(e) => set("mileageOrigin", e.target.value)}
                placeholder="e.g. Home office"
              />
              <label style={labelStyle}>Destination</label>
              <input
                style={{ ...inputStyle, marginBottom: 8 }}
                value={form.mileageDest || ""}
                onChange={(e) => set("mileageDest", e.target.value)}
                placeholder="e.g. Client site"
              />
              <label style={labelStyle}>Business Purpose</label>
              <input
                style={inputStyle}
                value={form.mileagePurpose || ""}
                onChange={(e) => set("mileagePurpose", e.target.value)}
                placeholder="e.g. Client meeting"
              />
            </div>
            <div style={{ fontSize: 11, color: "#2563EB", fontWeight: 600 }}>
              {(parseFloat(form.miles) || 0).toFixed(1)} mi × ${(parseFloat(form.mileageRate) || IRS_MILEAGE_RATE).toFixed(3)}/mi = ${((parseFloat(form.miles) || 0) * (parseFloat(form.mileageRate) || IRS_MILEAGE_RATE)).toFixed(2)}
            </div>
          </div>
        )}

        {/* Food & Dining extra fields */}
        {!isIncome && form.category === 'food_dining' && (
          <div style={{ background: "#F0FDF9", border: "1.5px solid #99F6E4", borderRadius: 12, padding: "14px 14px 10px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#0A7C8A", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12 }}>
              Dining Details
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Meal Type</label>
              <select
                style={inputStyle}
                value={form.mealType || ""}
                onChange={(e) => set("mealType", e.target.value)}
              >
                <option value="">— Select —</option>
                {["Breakfast", "Lunch", "Dinner", "Snacks", "Drinks"].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 2 }}>
              <label style={labelStyle}>Guest Name</label>
              <input
                style={{ ...inputStyle, marginBottom: 10 }}
                value={form.guestName || ""}
                onChange={(e) => set("guestName", e.target.value)}
                placeholder="e.g. Jane Doe"
              />
              <label style={labelStyle}>Guest Affiliation</label>
              <input
                style={inputStyle}
                value={form.guestAffiliation || ""}
                onChange={(e) => set("guestAffiliation", e.target.value)}
                placeholder="e.g. Acme Corp, Client"
              />
            </div>
          </div>
        )}

        {form.category !== 'mileage' && (
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Subtotal</label>
              <input
                style={inputStyle}
                type="number"
                step="0.01"
                value={form.subtotal || ""}
                onChange={(e) => setComponent("subtotal", e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Tax</label>
              <input
                style={inputStyle}
                type="number"
                step="0.01"
                value={form.tax || ""}
                onChange={(e) => setComponent("tax", e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
        )}

        {form.category !== 'mileage' && (
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Tip</label>
              <input
                style={inputStyle}
                type="number"
                step="0.01"
                value={form.tip || ""}
                onChange={(e) => setComponent("tip", e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
        )}

        <div>
          <label style={labelStyle}>Total</label>
          <input
            style={{ ...inputStyle, ...(hasMismatch ? mismatchStyle : {}) }}
            type="number"
            step="0.01"
            value={form.total || ""}
            onChange={(e) => set("total", e.target.value)}
            placeholder="0.00"
          />
        </div>

        {hasMismatch && (
          <div style={mismatchBannerStyle}>
            ⚠ Total ({form.total}) doesn't equal subtotal + tax + tip ({componentSum.toFixed(2)}). Please check the numbers.
          </div>
        )}

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Currency</label>
            <select
              style={inputStyle}
              value={form.currency || "USD"}
              onChange={(e) => set("currency", e.target.value)}
            >
              {CURRENCIES.map((cur) => (
                <option key={cur.code} value={cur.code}>
                  {cur.code} ({cur.symbol})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Date</label>
            <input
              style={inputStyle}
              type="date"
              value={form.date || ""}
              onChange={(e) => set("date", e.target.value)}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Start Date (opt)</label>
            <input
              style={inputStyle}
              type="date"
              value={form.start_date || ""}
              onChange={(e) => set("start_date", e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>End Date (opt)</label>
            <input
              style={inputStyle}
              type="date"
              value={form.end_date || ""}
              onChange={(e) => set("end_date", e.target.value)}
            />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Notes</label>
          <textarea
            style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
            value={form.notes || ""}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Add any notes..."
          />
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: "14px", borderRadius: 12,
              border: "1.5px solid #E5E7EB", background: "#fff",
              fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#6B7280",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              flex: 2, padding: "14px", borderRadius: 12, border: "none",
              background: "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)",
              fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#fff",
              boxShadow: "0 4px 15px rgba(5, 196, 176, 0.3)",
            }}
          >
            Save Expense
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1.5px solid #E5E7EB",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
  transition: "border-color 0.2s",
  colorScheme: "light",
};

const labelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: "#6B7280",
  letterSpacing: 0.8,
  marginBottom: 4,
  display: "block",
  textTransform: "uppercase",
};

const linkBtnStyle = {
  background: "none",
  border: "none",
  color: "#0A7C8A",
  fontSize: 11,
  fontWeight: 700,
  cursor: "pointer",
  padding: 0,
};

const newCatBoxStyle = {
  background: "#f5f3ff",
  border: "1.5px solid #c4b5fd",
  borderRadius: 10,
  padding: "12px",
};

const addBtnStyle = {
  width: "100%",
  padding: "8px",
  borderRadius: 8,
  border: "none",
  background: "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)",
  color: "#fff",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
};

const mismatchStyle = {
  borderColor: "#EF4444",
  background: "#FEF2F2",
};

const mismatchBannerStyle = {
  background: "#FEF2F2",
  border: "1.5px solid #FCA5A5",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 12,
  color: "#DC2626",
  fontWeight: 600,
  marginTop: -8,
};

export default ExpenseForm;
